<script setup>
import {useRoute} from "vue-router";
import {onMounted, ref} from "vue";
import FileService from "../../services/FileService.js";
import PDF from "pdf-vue3";
import Button from '../../components/forms/Button.vue';
import Select from '../../components/forms/Select.vue';
import DocumentTemplateService from "../../services/DocumentTemplateService.js";
import $ from 'jquery';
import {Dialog, DialogPanel, TransitionRoot} from '@headlessui/vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const route = useRoute();
let file = ref({});
let annotationMode = ref(false);
let selectedAnnotation = ref({});
let annotations = ref([]);
let annotationGroups = ref([]);
let annotationKeys = ref([]);
let showAnnotation = ref(false);
const vocabulary = ref([]);
const modalOpen = ref(false);

const annotationStyle = {
  position: 'absolute',
  color: 'rgb(0, 0, 0)',
  'font-family': 'Arial',
  'font-size': '20px',
  cursor: 'pointer',
  background: 'white',
  padding: ''
};

const defaultConfig = {
  color: [0, 0, 0],
  font: 'Arial',
  size: 10,
  style: ''
}

function fetchDocument(uuid) {
  FileService.getContent(uuid).then(response => {
    file.value = response;
  });
}

function fetchDocumentTemplate() {
  DocumentTemplateService.getDocumentTemplate(route.params.id, 'extend').then((response) => {
    file.value = {
      document_template_id: response.id,
      content: 'data:application/pdf;base64,' + response.template,
      name: response.template_name,
      annotations: response.annotations
    }
    showAnnotation.value = true;
  });
}

function setupAnnotations() {
  // setup Vocabulary
  DocumentTemplateService.getVocabulary().then(response => {
    vocabulary.value = response;
    annotationGroups.value = vocabulary.value.map((v) => ({
      value: v.group.value,
      label: v.group.label
    }));
  });

  // we have to wait until the annotation layer is loaded, because it is not loaded at the same time as the pdf
  // if we don't wait, only the first page will have annotations, others will disappear

  const interval = setInterval(() => {
    const pages = document.querySelectorAll('.pdf-vue3-canvas-container canvas');
    if (pages.length > 0) {
      clearInterval(interval);
    }
    pages.forEach((element, key) => {
      $(element).attr('page', key + 1);
      if (file.value.annotations?.fields?.length > 0) {
        file.value.annotations.fields.forEach((annotation) => {
          if (annotation.page === key + 1) {
            // convert coords from percent to pixels
            const Xpx = annotation.x / 100 * $(element).width();
            const Ypx = (annotation.y / 100 * $(element).height()) + ((key) * $(element).height());
            annotation.key = annotation.key.replace('${', '').replace('}', '');

            const selectedAnnotationField = vocabulary.value.find((v) => v.group.value === annotation.group)?.fields.find((f) => f.value === annotation.key).label;
            document.querySelector('.pdf-vue3-canvas-container').insertAdjacentHTML('beforeend', `<span id="${'annotation-' + annotations.value.length.toString()}" page="${annotation.page}" class="pdf-annotation" style="left: ${Xpx}px; top: ${Ypx}px;">${selectedAnnotationField}</span>`);

            const createdAnnotation = document.getElementById('annotation-' + annotations.value.length.toString());
            Object.assign(createdAnnotation.style, annotationStyle);
            makeElementDraggable(createdAnnotation);
            annotations.value.push({
              ...annotation,
              id: annotations.value.length,
              x: Xpx,
              y: Ypx
            });

          }
        });

        $('.pdf-annotation').addClass('pointer-events-none');
      }});
  }, 500);

  document.querySelector('.pdf-vue3-canvas-container').addEventListener('click', (event) => {
    if (annotationMode.value) {
      const target = event.target;
      const page = $(target).attr('page');
      const pageHeight = $(target).height();

      // edit annotation
      if (target.tagName === 'SPAN' && target.classList?.contains('pdf-annotation')) {
        // if element is dragging, we don't want to open the modal
        if (target.classList?.contains('dragging')) {
          target.classList?.remove('dragging');
          return;
        }

        selectedAnnotation.value = annotations.value.find((a) => a.id === Number(target.id.split('-')[1]));
        openAnnotationModal();
        onAnnotationGroupSelect();

        return;
      }

      // create new annotation
      const x = (event.offsetX);
      const y = (event.offsetY + ((page - 1) * pageHeight));
      selectedAnnotation.value = {x, y, page: parseInt(page)};
      openAnnotationModal();
    }
  });
}

onMounted(() => {
  if (document.location.href.includes('documents/templates') && route.params.id) {
    fetchDocumentTemplate();
  } else {
    fetchDocument(route.params.uuid);
  }
});

function saveAnnotation() {
  const selectedAnnotationField = vocabulary.value.find((v) => v.group.value === selectedAnnotation.value.group)?.fields.find((f) => f.value === selectedAnnotation.value.key).label;
  // edit
  if (selectedAnnotation.value.id >= 0) {
    const editedAnnotation = annotations.value.find((a) => a.id === selectedAnnotation.value.id);
    editedAnnotation.group = selectedAnnotation.value.group;
    editedAnnotation.key = selectedAnnotation.value.key;
    document.getElementById('annotation-' + selectedAnnotation.value.id).innerText = selectedAnnotationField;
    hideAnnotationModal();
  }
  // create
  else {
    document.querySelector('.pdf-vue3-canvas-container').insertAdjacentHTML('beforeend', `<span id="${'annotation-' + annotations.value.length.toString()}" page="${1}" class="pdf-annotation" style="left: ${selectedAnnotation.value.x}px; top: ${selectedAnnotation.value.y}px;">${selectedAnnotationField}</span>`);

    const createdAnnotation = document.getElementById('annotation-' + annotations.value.length.toString());
    Object.assign(createdAnnotation.style, annotationStyle);
    makeElementDraggable(createdAnnotation);

    annotations.value.push({
      id: annotations.value.length,
      ...selectedAnnotation.value
    });

    hideAnnotationModal();
  }
}

function openAnnotationModal() {
  modalOpen.value = true;
}

function hideAnnotationModal() {
  modalOpen.value = false;
  selectedAnnotation.value = {};
}

function onAnnotationGroupSelect() {
  const keys = vocabulary.value.find((v) => v.group.value === selectedAnnotation.value.group)?.fields;
  annotationKeys.value = keys?.map((k) => ({
    value: k.value,
    label: k.label
  }));
}

function onSubmitAnnotations() {
  const pageWidth = $('.pdf-vue3-canvas-container canvas').width();
  const pageHeight = $('.pdf-vue3-canvas-container canvas').height();
  const annotationsToSend = [];
  annotations.value.forEach((annotation) => {
    annotationsToSend.push({
      ...annotation,
      key: '${' + annotation.key + '}',
      x: +(annotation.x / pageWidth * 100).toFixed(2),
      y: +((annotation.y / pageHeight * 100) % 100).toFixed(2)
    });
  });

  FileService.saveAnnotations(file.value.document_template_id, annotationsToSend, defaultConfig).then(() => {
    toggleAnnotationMode();
  });
}

function makeElementDraggable(element) {
  const position = {pos1: 0, pos2: 0, pos3: 0, pos4: 0};
  if (document.getElementById(element.id + 'header')) {
    // if present, the header is where you move the DIV from:
    document.getElementById(element.id + 'header').onmousedown = (e) => dragMouseDown(e, element, position);
  } else {
    // otherwise, move the DIV from anywhere inside the DIV:
    element.onmousedown = (e) => dragMouseDown(e, element, position);
  }
}

function dragMouseDown(e, element, position) {
  e.preventDefault();
  // get the mouse cursor position at startup:
  position.pos3 = e.clientX;
  position.pos4 = e.clientY;
  document.onmouseup = () => closeDragElement(element, position);
  // call a function whenever the cursor moves:
  document.onmousemove = (event) => elementDrag(event, element, position);
}

function elementDrag(e, element, position) {
  element.classList.add('dragging');
  e.preventDefault();
  // calculate the new cursor position:
  position.pos1 = position.pos3 - e.clientX;
  position.pos2 = position.pos4 - e.clientY;
  position.pos3 = e.clientX;
  position.pos4 = e.clientY;

  // set the element's new position:
  element.style.top = (element.offsetTop - position.pos2) + 'px';
  element.style.left = (element.offsetLeft - position.pos1) + 'px';
}

function closeDragElement(element, position) {
  // stop moving when mouse button is released:
  const selectedAnnotation = annotations.value.find((a) => a.id === Number(element.id.split('-')[1]));
  let x = (element.offsetLeft - position.pos1);
  let y = (element.offsetTop - position.pos2);
  const pageNumber = selectedAnnotation.page;
  const pageWidth = $('.pdf-vue3-canvas-container canvas').width();
  const pageHeight = $('.pdf-vue3-canvas-container canvas').height();

  if (x > pageWidth - 100) {
    x = pageWidth - 100;
    element.style.left = x + 'px';
  }
  if (x < 0) {
    x = 0;
    element.style.left = x + 'px';
  }
  if (y < (pageNumber - 1) * pageHeight) {
    y = (pageNumber - 1) * pageHeight;
    element.style.top = y + 'px';
  }
  if (y > (pageNumber * pageHeight) - 40) {
    y = (pageNumber * pageHeight) - 40;
    element.style.top = y + 'px';
  }

  selectedAnnotation.x = x;
  selectedAnnotation.y = y;
  document.onmouseup = null;
  document.onmousemove = null;
}

function toggleAnnotationMode() {
  annotationMode.value = !annotationMode.value;
  document.querySelectorAll('.pdf-annotation').forEach((el) => el.classList.toggle('pointer-events-none'));
}

function deleteAnnotation() {
  annotations.value = annotations.value.filter((a) => a.id !== selectedAnnotation.value.id);
  document.getElementById('annotation-' + selectedAnnotation.value.id).remove();
  hideAnnotationModal();
}
</script>

<template>
  <!-- modals -->
  <TransitionRoot appear :show="modalOpen" as="template">
    <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered"  style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <h3 class="text-lg font-bold">{{ selectedAnnotation.id >= 0 ? t('pdfViewer.anotation_edit') : t('pdfViewer.anotation_create') }}</h3>
            </div>
            <div class="modal-body">
              <form @submit.prevent="saveAnnotation">
                <div class="mt-3">
                  <div class="row mt-3">
                    <Select v-model="selectedAnnotation.group" :options="annotationGroups" @change="onAnnotationGroupSelect" :label="t('pdfViewer.group')" class="w-full"/>
                  </div>
                  <div class="row mt-3">
                    <Select v-model="selectedAnnotation.key" :options="annotationKeys" :label="t('pdfViewer.key')" class="w-full"/>
                  </div>
                  <div class="modal-action">
                    <Button type="submit" :label="t('pdfViewer.save')"></Button>
                    <Button :label="t('pdfViewer.close')" class="btn ms-2" type="button" @click="hideAnnotationModal"></Button>
                    <button class="btn ms-2 btn-danger" type="button" @click="deleteAnnotation">{{ t('pdfViewer.delete') }}</button>
                  </div>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  <!-- end modals -->


  <div class="px-5 py-5">
    <h1 class="text-3xl overview-title mb-3">{{ file.name }}</h1>
    <div v-if="showAnnotation" class="flex justify-between">
      <div>
        <p v-if="annotationMode" class="text-dark mb-0">{{ t('pdfViewer.anotation_mode') }}</p>
        <div v-if="annotationMode"><small class="text-muted"> {{ t('pdfViewer.insert_anotation_desired_location') }} </small></div>
      </div>
      <div>
        <button @click="toggleAnnotationMode()" v-if="!annotationMode" class="btn btn-primary">{{ t('pdfViewer.anotation_mode') }}</button>
        <button @click="onSubmitAnnotations" v-if="annotationMode" class="btn btn-primary">{{ t('pdfViewer.anotation_save') }}</button>
      </div>
    </div>
    <div class="py-3" :style="showAnnotation ? 'width: 1200px;' : ''">
      <PDF @on-pdf-init="setupAnnotations" :src="file.content"/>
    </div>
  </div>
</template>

<style scoped>


</style>
